import React from "react";
import { waLink } from "@/lib/brand";

const SIGNATURES = [
  {
    slug: "nodi-badher-raat",
    bengali: "নদী বাঁধের রাত",
    name: "Nodi Badher Raat",
    subtitle: "The river embankment's night",
    time: "DUSK · FOR TWO",
    tone: "block-placeholder",
    body:
      "A table set on the nodi badh — the raised earthen dyke that runs along the tidal river at the property's edge — at dusk, for two. Earthen oil lamps along the embankment on both sides of the table. The Sundarbans river on one side. The mangrove canopy on the other. The kitchen prepares a fixed menu drawn entirely from what the farm held that day. No printed card. The host describes the dishes before each arrives. The meal ends when the guests decide it ends.",
    ask: "Hello FarmStory, I'd like to ask about Nodi Badher Raat.",
  },
  {
    slug: "bhor-alap",
    bengali: "ভোর আলাপ",
    name: "Bhor Alap",
    subtitle: "The dawn's opening note",
    time: "APPROX 5:20 AM · SUR MACHAAN",
    tone: "block-placeholder--river",
    body:
      "On a bamboo platform raised over the surface of the West Gawa — a structure built specifically for this, light enough that it sits on the water without disturbing it — a bansuri player begins before the light. The music starts at approximately 5:20 AM and plays until the sun has fully cleared the treeline. It is not a performance. There is no audience arrangement, no chairs set up, no announcement. The music simply begins and the property wakes to it. The platform is called the Sur Machaan.",
    ask: "Hello FarmStory, I'd like to ask about Bhor Alap.",
  },
  {
    slug: "shekorer-bhor",
    bengali: "শেকড়ের ভোর",
    name: "Shekorer Bhor",
    subtitle: "The roots' dawn",
    time: "DEPARTS 4:45 AM · WITH A GUIDE",
    tone: "block-placeholder--dark",
    body:
      "A walk to the mangrove forest at the property's boundary, departing at 4:45 AM, arriving at the forest edge as the light begins. The mangrove aerial roots at this hour do something available at no other time: the water below them is dark, the sky above them is beginning to lighten. A forest-knowledgeable guide from the adjacent community leads this. The guide does not narrate. They navigate and they know when to stop and be still.",
    ask: "Hello FarmStory, I'd like to ask about Shekorer Bhor.",
  },
  {
    slug: "andharey-nadi",
    bengali: "অন্ধারে নদী",
    name: "Andharey Nadi",
    subtitle: "The river in the dark",
    time: "APPROX 9 PM · BY WOODEN BOAT",
    tone: "block-placeholder--dark",
    body:
      "A small wooden boat on the tidal river at night, after dinner, at approximately 9 PM when the property has settled and the Adda is quieting. The river at this hour has no other light source within sight. The Sundarbans sky is the kind of sky that urban guests have no physiological memory of. If the season permits it, there is phosphorescence in the water — a pale blue-green disturbed light that trails the oar and the hull. If the season does not permit it, there is only the dark and the current and the mangrove silhouettes and the specific sounds of the delta at night, which are not silence. The boatman is from the fishing community. He rows. The guests sit.",
    ask: "Hello FarmStory, I'd like to ask about Andharey Nadi.",
  },
  {
    slug: "bhorbelaar-khal",
    bengali: "ভোরবেলার খাল",
    name: "Bhorbela'r Khal",
    subtitle: "The creek at dawn",
    time: "BEFORE SUNRISE · FLAT-BOTTOMED BOAT",
    tone: "block-placeholder--river",
    body:
      "The tidal khals — the small branching waterways that feed off the main river — at the hour just before sunrise, taken by flat-bottomed wooden boat. The khals are narrow enough that the mangrove canopy closes overhead in places. The light arrives sideways, through gaps in the roots, before it arrives from above. Birds are using these waterways as flyways. The boat moves slowly enough that nothing is disturbed. The return is timed to arrive back at the property as the Sur Machaan begins.",
    ask: "Hello FarmStory, I'd like to ask about Bhorbela'r Khal.",
  },
];

const ALONGSIDE = [
  {
    slug: "bhorer-pakhi",
    bengali: "ভোরের পাখি",
    name: "Bhor'er Pakhi",
    subtitle: "Bird watching, from the nodi badh",
    time: "FIRST LIGHT · WITH A NATURALIST",
    tone: "block-placeholder--cream",
    body:
      "From the nodi badh specifically — the elevated position gives simultaneous access to the river surface, the mangrove canopy, and the open sky above the fields. The Sundarbans holds collared kingfishers, brahminy kites, mangrove whistlers, white-bellied sea eagles, purple herons, lesser adjutant storks. A naturalist from the region accompanies. They name what they see in Bengali first, English second, because the Bengali names are the ones attached to the memory of the place.",
    ask: "Hello FarmStory, I'd like to ask about Bhor'er Pakhi (bird watching).",
  },
  {
    slug: "chaya-y-bhoj",
    bengali: "ছায়ায় ভোজ",
    name: "Chaya-y Bhoj",
    subtitle: "The shade meal, in the phalbagan",
    time: "MID-MORNING · UNDER TWO HUNDRED TREES",
    tone: "block-placeholder--cream",
    body:
      "A meal laid out in the Phalbagan under the canopy — not a table and chairs but bamboo mats on the ground, floor-level, under two hundred fruit trees. Seasonal fruit from the trees directly above. Simple food from the Unun: posto and rice, pickle board, whatever the farm held that day. The meal is assembled in the morning when the orchard is coolest. Available only when the phalbagan is in fruit — which, across two hundred trees, is most of the year. Chaya is shade, and is already the name of the gathering point within the orchard.",
    ask: "Hello FarmStory, I'd like to ask about Chaya-y Bhoj (the shade meal).",
  },
  {
    slug: "jeler-sathe",
    bengali: "জেলের সাথে",
    name: "Jele'r Sathe",
    subtitle: "With the fisherman",
    time: "BEFORE DAWN · ON THE FISHERMAN'S BOAT",
    tone: "block-placeholder--clay",
    body:
      "Going out before dawn with one of the fishermen from the adjacent community — not as a tourist, not on a separate boat arranged for guests, but on the fisherman's own boat, doing what the fisherman does. Net cast or trap check depending on what the fisherman is working that morning. The catch, whatever it is, goes with the fisherman. FarmStory does not take the catch for the kitchen — that would make the fisherman a supplier, not a host.",
    ask: "Hello FarmStory, I'd like to ask about Jele'r Sathe.",
  },
];

function ExperienceCard({ exp, index }) {
  const flip = index % 2 === 1;
  return (
    <article
      className="grid md:grid-cols-12 gap-8 md:gap-14 items-stretch"
      data-testid={`experience-${exp.slug}`}
    >
      <div
        className={`md:col-span-6 rounded-[8px] overflow-hidden hair-all ${flip ? "md:order-2" : ""}`}
      >
        <div className={`block-placeholder ${exp.tone}`} style={{ height: 380 }} />
      </div>
      <div className={`md:col-span-6 flex flex-col justify-center ${flip ? "md:order-1" : ""}`}>
        <div className="label-track" style={{ color: "var(--sundari-gold)" }}>
          {exp.time}
        </div>
        <div className="italic-display mt-4" style={{ fontSize: "clamp(24px, 2.4vw, 30px)", opacity: 0.8 }}>
          {exp.bengali}
        </div>
        <h3
          className="italic-display mt-1"
          style={{ fontSize: "clamp(34px, 4.4vw, 54px)", lineHeight: 1.02 }}
        >
          {exp.name}
        </h3>
        <div
          className="label-track mt-3"
          style={{ color: "var(--delta-clay)" }}
        >
          {exp.subtitle}
        </div>
        <hr className="divider-gold mt-6" />
        <p className="mt-6" style={{ fontSize: "15.5px", lineHeight: 1.75 }}>
          {exp.body}
        </p>
        <div className="mt-8">
          <a
            href={waLink(exp.ask)}
            target="_blank"
            rel="noreferrer"
            className="btn-pill btn-pill--dark"
            data-testid={`experience-cta-${exp.slug}`}
          >
            Ask About This
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Experiences() {
  return (
    <div data-testid="page-experiences">
      {/* Hero */}
      <section style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-36 pb-20 md:pb-28">
          <div
            className="italic-display mb-3"
            style={{ fontSize: "clamp(18px, 2vw, 24px)", color: "var(--sundari-gold)", opacity: 0.9 }}
            data-testid="page-bengali"
          >
            অভিজ্ঞতা
          </div>
          <div className="eyebrow" style={{ color: "var(--sundari-gold)" }}>Experiences</div>
          <h1
            className="italic-display mt-6"
            style={{ fontSize: "clamp(48px, 7.5vw, 108px)", lineHeight: 0.98 }}
          >
            The delta,
            <br />
            <span style={{ color: "var(--sundari-gold)" }}>at its hours.</span>
          </h1>
          <p className="mt-8 max-w-[720px]" style={{ fontSize: "17px", lineHeight: 1.75, opacity: 0.88 }}>
            Wonder is not manufactured here; it is found. These are the moments the farm and the
            river hold for us — dawn, dusk, the dark of the tide. They are led by the people who
            know them best, and they happen at the hours that suit them, not the ones that suit a
            schedule.
          </p>
        </div>
      </section>

      {/* Signature */}
      <section style={{ background: "var(--salt-white)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Signature Experiences</div>
              <h2 className="italic-display mt-4" style={{ fontSize: "clamp(30px, 3.6vw, 44px)", lineHeight: 1.05 }}>
                Five moments the farm keeps.
              </h2>
            </div>
            <div className="hidden md:block label-track" style={{ color: "var(--delta-clay)", opacity: 0.7 }}>
              01 — 05
            </div>
          </div>
          <div className="space-y-24 md:space-y-32">
            {SIGNATURES.map((exp, i) => (
              <ExperienceCard key={exp.slug} exp={exp} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Alongside */}
      <section style={{ background: "var(--bamboo-cream)" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="eyebrow" style={{ color: "var(--delta-clay)" }}>Alongside</div>
              <h2 className="italic-display mt-4" style={{ fontSize: "clamp(30px, 3.6vw, 44px)", lineHeight: 1.05 }}>
                Three that let you in.
              </h2>
            </div>
            <div className="hidden md:block label-track" style={{ color: "var(--delta-clay)", opacity: 0.7 }}>
              06 — 08
            </div>
          </div>
          <div className="space-y-24 md:space-y-32">
            {ALONGSIDE.map((exp, i) => (
              <ExperienceCard key={exp.slug} exp={exp} index={i + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section style={{ background: "var(--mangrove-night)", color: "var(--bamboo-cream)" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <blockquote
            className="italic-display"
            style={{ fontSize: "clamp(24px, 3vw, 34px)", lineHeight: 1.35 }}
            data-testid="experiences-pullquote"
          >
            &ldquo;The music simply begins and the property wakes to it.&rdquo;
          </blockquote>
          <div className="mx-auto mt-10 divider-gold" />
          <div className="mt-10">
            <a
              href={waLink("Hello FarmStory, I'd like to plan a stay and ask about the experiences.")}
              target="_blank"
              rel="noreferrer"
              className="btn-pill btn-pill--solid"
              data-testid="experiences-cta-whatsapp"
            >
              Plan a Stay on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
